import React, {useEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {JobPostingRequest} from '../../interface/JobPostingRequest';
import {getJobPostingSettings} from '../../api/jobApi';
import {JobPostingSettings} from "../../interface/JobPostingSettings";
import {defaultAlert} from "../../utils/alert";
import {setComma, unComma} from "../../utils/format";
import {useIosKeyboardOffset} from '../../hooks/useIosKeyboardOffset';
import {useIosInputAutoScroll} from '../../hooks/useIosInputAutoScroll';
import DaumPostcode, {DaumPostcodeData} from 'react-daum-postcode';

interface JobQuestion {
    questionCode: string;
    questionName: string;
    questionType: 'O' | 'T' | 'M';
    required?: boolean;
    options?: { optionName: string }[];
}

interface JobPostingStep2Props {
    form: JobPostingRequest;
    onFormChange: (updatedForm: JobPostingRequest) => void;
    questionList: JobQuestion[];
    answers: { [key: string]: string | { selection: string; text: string } };
    onAnswerChange: (questionCode: string, value: string | { selection: string; text: string }) => void;
    onPrevStep: () => void;
    onSubmit: () => void;
}

const JobPostingStep2 = ({
                             form,
                             onFormChange,
                             questionList,
                             answers,
                             onAnswerChange,
                             onPrevStep,
                             onSubmit,
                         }: JobPostingStep2Props) => {
    const [settings, setSettings] = useState<JobPostingSettings | null>(null);
    const [loading, setLoading] = useState(true);

    const keyboardHeight = useIosKeyboardOffset();
    useIosInputAutoScroll();

    // 다음 주소 검색 팝업 state
    const [showPostcode, setShowPostcode] = useState(false);

    // 주소검색 팝업 열릴 때 body 스크롤 잠금
    useEffect(() => {
        if (showPostcode) {
            document.body.style.overflow = 'hidden';
            document.body.style.position = 'fixed';
            document.body.style.width = '100%';
        } else {
            document.body.style.overflow = '';
            document.body.style.position = '';
            document.body.style.width = '';
        }
        return () => {
            document.body.style.overflow = '';
            document.body.style.position = '';
            document.body.style.width = '';
        };
    }, [showPostcode]);
    // 전체 도로명 주소 표시용
    const [roadAddress, setRoadAddress] = useState('');
    // 도로명 주소에서 시/도 구/군 제외한 나머지 (선유로 49길 23)
    const [roadDetail, setRoadDetail] = useState('');
    // 사용자가 직접 입력하는 상세주소 (502호 등)
    const [userDetail, setUserDetail] = useState('');

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const response = await getJobPostingSettings();
                setSettings(response.data);
            } catch (error) {
                console.error('설정값 로딩 실패:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchSettings();
    }, []);

    // 재등록 시 기존 주소 복원
    const initializedRef = useRef(false);
    useEffect(() => {
        if (initializedRef.current) return;
        if (!form.address) {
            initializedRef.current = true;
            return;
        }
        initializedRef.current = true;

        // 기존 addressDetail에서 도로명 부분과 사용자 입력 부분 분리
        if (form.addressDetail) {
            const commaIdx = form.addressDetail.indexOf(',');
            if (commaIdx > -1) {
                setRoadDetail(form.addressDetail.substring(0, commaIdx).trim());
                setUserDetail(form.addressDetail.substring(commaIdx + 1).trim());
            } else {
                setRoadDetail(form.addressDetail);
            }
            setRoadAddress(`${form.address} ${form.addressDetail}`);
        }
    }, [form.address, form.addressDetail]);

    const updateForm = (updates: Partial<JobPostingRequest>) => {
        onFormChange({...form, ...updates});
    };

    // 다음 주소 검색 완료 핸들러
    const handlePostcodeComplete = (data: DaumPostcodeData) => {
        // address = "시/도 구/군" (예: "서울특별시 영등포구")
        const address = `${data.sido} ${data.sigungu}`;

        // 전체 도로명 주소에서 시/도 구/군 부분 제거 → 나머지 도로명
        const fullRoadAddress = data.roadAddress || data.address;
        const restAddress = fullRoadAddress
            .replace(data.sido, '')
            .replace(data.sigungu, '')
            .trim();

        setRoadAddress(fullRoadAddress);
        setRoadDetail(restAddress);
        setUserDetail('');
        updateForm({
            address: address,
            addressDetail: restAddress
        });
        setShowPostcode(false);
    };

    // 사용자 상세주소 입력 시 addressDetail 업데이트 (도로명 나머지 + 사용자 입력)
    const handleUserDetailChange = (value: string) => {
        setUserDetail(value);
        const combined = roadDetail
            ? `${roadDetail}${value ? ', ' + value : ''}`
            : value;
        updateForm({addressDetail: combined});
    };

    // Validation 함수
    const validateForm = (): boolean => {
        if (!settings) {
            defaultAlert('설정 정보를 불러오는 중입니다. 잠시 후 다시 시도해주세요.');
            return false;
        }

        // 1. 서비스 유형 검증
        if (!form.workType) {
            defaultAlert('서비스 유형을 선택해주세요.');
            return false;
        }
        const startDate = new Date(form.careStartDate);
        startDate.setHours(0, 0, 0, 0);
        const endDate = new Date(form.careEndDate);
        endDate.setHours(0, 0, 0, 0);
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (form.workType === 'DAILY') {
            // 종일제: 시작일은 반드시 내일 이후
            if (startDate <= today) {
                defaultAlert('종일제 돌봄 시작일은 오늘 이후여야 합니다.');
                return false;
            }
        } else if (form.workType === 'HOURLY') {
            // 시간제: 시작일은 오늘 포함 이후
            if (startDate < today) {
                defaultAlert('돌봄 시작일은 오늘 이후여야 합니다.');
                return false;
            }
            // 시작일이 오늘이면 업무 시작 시간이 현재 시각 이후여야 함
            const isStartToday = startDate.getTime() === today.getTime();
            if (isStartToday && form.workStartTime) {
                const now = new Date();
                const [sh, sm] = form.workStartTime.split(':').map(Number);
                const workStart = new Date();
                workStart.setHours(sh, sm, 0, 0);
                if (workStart <= now) {
                    defaultAlert('현재시간 이후의 시간부터 선택할 수 있습니다.');
                    return false;
                }
            }
        }

        if (endDate < startDate) {
            defaultAlert('돌봄 종료일은 시작일보다 늦어야 합니다.');
            return false;
        }

        const daysDiff = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;
        // 2. 시간제 근무시간 검증
        if (form.workType === 'HOURLY') {
            if (!form.workStartTime || !form.workEndTime) {
                defaultAlert('근무 시작 시간과 종료 시간을 선택해주세요.');
                return false;
            }

            const [startHour, startMin] = form.workStartTime.split(':').map(Number);
            const [endHour, endMin] = form.workEndTime.split(':').map(Number);
            const startMinutes = startHour * 60 + startMin;
            const endMinutes = endHour * 60 + endMin;
            const workHours = (endMinutes - startMinutes) / 60;

            if (workHours <= 0) {
                defaultAlert('종료 시간은 시작 시간보다 늦어야 합니다.');
                return false;
            }

            if (workHours < settings.hourlyMinHours) {
                defaultAlert(`시간제는 최소 ${settings.hourlyMinHours}시간 이상 근무해야 합니다.`);
                return false;
            }

            if (workHours > settings.hourlyMaxHours) {
                defaultAlert(`시간제는 최대 ${settings.hourlyMaxHours}시간까지 근무 가능합니다.`);
                return false;
            }
            if (daysDiff < settings.dailyMinDays) {
                defaultAlert(`시간제는 최소 ${settings.dailyMinDays}일 이상 등록해야 합니다.`);
                return false;
            }
            if (daysDiff > settings.dailyMaxDays) {
                defaultAlert(`시간제는 최대 ${settings.dailyMaxDays}일까지 등록 가능합니다.`);
                return false;
            }
        }

        // 3. 돌봄 기간 검증
        if (!form.careStartDate || !form.careEndDate) {
            defaultAlert('돌봄 시작일과 종료일을 선택해주세요.');
            return false;
        }

        if (form.workType === 'DAILY') {
            if (daysDiff < settings.dailyMinDays) {
                defaultAlert(`종일제는 최소 ${settings.dailyMinDays}일 이상 등록해야 합니다.`);
                return false;
            }
            if (daysDiff > settings.dailyMaxDays) {
                defaultAlert(`종일제는 최대 ${settings.dailyMaxDays}일까지 등록 가능합니다.`);
                return false;
            }
        }

        // 4. 돌봄 요청 장소 검증
        if (!form.carePlaceType) {
            defaultAlert('돌봄 요청 장소를 선택해주세요.');
            return false;
        }

        if (form.carePlaceType === 'HOSPITAL' && !form.hospitalName?.trim()) {
            defaultAlert('병원명을 입력해주세요.');
            return false;
        }

        if (!form.address?.trim()) {
            defaultAlert('주소를 입력해주세요.');
            return false;
        }

        if (!form.addressDetail?.trim()) {
            defaultAlert('상세 주소를 입력해주세요.');
            return false;
        }

        // 5. 어르신 상태 정보 검증 (필수 항목)
        for (const question of questionList) {
            if (question.required) {
                const answer = answers[question.questionCode];

                if (!answer) {
                    defaultAlert(`${question.questionName}은(는) 필수 항목입니다.`);
                    return false;
                }

                if (question.questionType === 'M') {
                    if (typeof answer !== 'object' || !answer.selection?.trim()) {
                        defaultAlert(`${question.questionName}을(를) 선택해주세요.`);
                        return false;
                    }
                    if ((answer.selection.includes('기타') || answer.selection.includes('직접')) && !answer.text?.trim()) {
                        defaultAlert(`${question.questionName}의 추가 의견을 입력해주세요.`);
                        return false;
                    }
                } else if (question.questionType === 'O' || question.questionType === 'T') {
                    if (typeof answer !== 'string' || !answer.trim()) {
                        defaultAlert(`${question.questionName}을(를) 입력해주세요.`);
                        return false;
                    }
                }
            }
        }

        // 6. 돌봄 급여 검증
        if (!form.payNegotiable) {
            defaultAlert('급여 협의 가능 여부를 선택해주세요.');
            return false;
        }

        if (!form.dailyPay?.trim()) {
            defaultAlert('급여를 입력해주세요.');
            return false;
        }

        const payAmount = parseInt(form.dailyPay.replace(/,/g, ''));
        if (isNaN(payAmount) || payAmount <= 0) {
            defaultAlert('올바른 급여 금액을 입력해주세요.');
            return false;
        }

        // 급여 범위 권장 (경고만)
        const baseWage = form.workType === 'DAILY'
            ? (form.carePlaceType === 'HOME' ? settings.careDailyWage : settings.hospitalDailyWage)
            : (form.carePlaceType === 'HOSPITAL' ? settings.hospitalHourlyWage : settings.careHourlyWage);

        const minWage = baseWage - (baseWage * settings.proposalRangeMinus / 100);
        const maxWage = baseWage + (baseWage * settings.proposalRangePlus / 100);

        if (payAmount < minWage || payAmount > maxWage) {
            defaultAlert(`입력하신 급여(${payAmount.toLocaleString()}원)가 \n권장 범위(${minWage.toLocaleString()}원 ~ ${maxWage.toLocaleString()}원)를 벗어났습니다.`);
            return false;

        }

        return true;
    };

    // 제출 핸들러
    const handleSubmit = () => {
        if (validateForm()) {
            onSubmit();
        }
    };

    // 급여 평균 계산 함수
    const getAverageWage = () => {
        if (!settings) return '계산 중...';

        try {
            const wage = form.workType === 'DAILY'
                ? (form.carePlaceType === 'HOME' ? settings.careDailyWage : settings.hospitalDailyWage)
                : (form.carePlaceType === 'HOSPITAL' ? settings.hospitalHourlyWage : settings.careHourlyWage);

            if (wage === undefined || wage === null || isNaN(wage)) {
                return '정보 없음';
            }

            return `${wage.toLocaleString()}원`;
        } catch (error) {
            console.error('급여 계산 오류:', error);
            return '정보 없음';
        }
    };

    // 급여 권장 범위 계산 함수
    const getWageRange = () => {
        if (!settings) return null;

        try {
            const baseWage = form.workType === 'DAILY'
                ? (form.carePlaceType === 'HOME' ? settings.careDailyWage : settings.hospitalDailyWage)
                : (form.carePlaceType === 'HOSPITAL' ? settings.hospitalHourlyWage : settings.careHourlyWage);

            if (baseWage === undefined || baseWage === null || isNaN(baseWage)) {
                return null;
            }

            const proposalMinus = settings.proposalRangeMinus || 0;
            const proposalPlus = settings.proposalRangePlus || 0;

            const minWage = Math.floor(baseWage - (baseWage * proposalMinus / 100));
            const maxWage = Math.floor(baseWage + (baseWage * proposalPlus / 100));

            return {min: minWage, max: maxWage};
        } catch (error) {
            console.error('급여 범위 계산 오류:', error);
            return null;
        }
    };

    const renderQuestionSection = () => (
        <div className="round-box">
            <div className="round-header">
                <h3 className="round-tit">어르신 상태 정보</h3>
            </div>
            <div className="round-body">
                {questionList.map((question) => {
                    const answer = answers[question.questionCode];
                    const isAnswered = (() => {
                        if (!answer) return false;

                        if (question.questionType === 'M') {
                            return typeof answer === 'object' && answer.selection?.trim();
                        }

                        return typeof answer === 'string' ? answer.trim() : false;
                    })();
                    const isRequired = question.required;

                    return (
                        <div className="form-item" key={question.questionCode}>
                            <label className="input-tit">
                                {question.questionName}
                                {isRequired && <span className="point">*</span>}
                            </label>
                            {question.questionType === 'O' && (
                                <div className="line-input-box">
                                    <select
                                        value={typeof answer === 'string' ? answer : ''}
                                        onChange={(e) =>
                                            onAnswerChange(
                                                question.questionCode,
                                                e.target.value
                                            )
                                        }
                                        className={!isAnswered && isRequired ? 'error' : ''}
                                    >
                                        <option value="">선택</option>
                                        {question.options?.map((opt, oIdx) => (
                                            <option
                                                key={oIdx}
                                                value={opt.optionName}
                                            >
                                                {opt.optionName}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {question.questionType === 'T' && (
                                <div className="input-box">
                                    <textarea
                                        value={typeof answer === 'string' ? answer : ''}
                                        onChange={(e) =>
                                            onAnswerChange(
                                                question.questionCode,
                                                e.target.value
                                            )
                                        }
                                        className={!isAnswered && isRequired ? 'error' : ''}
                                    />
                                </div>
                            )}

                            {question.questionType === 'M' && (
                                <div className="composite-input-box">
                                    <div className="line-input-box">
                                        <select
                                            value={typeof answer === 'object' ? answer.selection : ''}
                                            onChange={(e) => {
                                                onAnswerChange(
                                                    question.questionCode,
                                                    {
                                                        selection: e.target.value,
                                                        text: typeof answer === 'object' ? answer.text : ''
                                                    }
                                                );
                                            }}
                                            className={!isAnswered && isRequired ? 'error' : ''}
                                        >
                                            <option value="">선택</option>
                                            {question.options?.map((opt, oIdx) => (
                                                <option key={oIdx} value={opt.optionName}>
                                                    {opt.optionName}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {typeof answer === 'object' &&
                                        answer.selection &&
                                        (answer.selection.includes('기타') || answer.selection.includes('직접')) && (
                                            <div className="input-box" style={{marginTop: '10px'}}>
                                                <textarea
                                                    value={answer.text || ''}
                                                    onChange={(e) =>
                                                        onAnswerChange(
                                                            question.questionCode,
                                                            {
                                                                selection: answer.selection,
                                                                text: e.target.value
                                                            }
                                                        )
                                                    }
                                                    placeholder="추가 의견을 입력해주세요"
                                                />
                                            </div>
                                        )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );

    // 로딩 중일 때
    if (loading) {
        return (
            <main className="container type_gray">
                <section>
                    <div className="inner-box">
                        <p style={{textAlign: 'center', padding: '50px 0'}}>
                            설정 정보를 불러오는 중입니다...
                        </p>
                    </div>
                </section>
            </main>
        );
    }

    return (
        <>
            <main className="container type_gray">
                <section>
                    <div className="inner-box">
                        <form className="form-box" onSubmit={e => e.preventDefault()}>
                            {/* 서비스 유형 */}
                            <div className="round-box">
                                <div className="round-header">
                                    <h3 className="round-tit">서비스 유형</h3>
                                </div>
                                <div className="round-body">
                                    <div className="form-item">
                                        <div className="radio-row">
                                            <span className="text-radio">
                                                <input
                                                    type="radio"
                                                    id="radio1"
                                                    name="workType"
                                                    checked={form.workType === 'DAILY'}
                                                    onChange={() =>
                                                        updateForm({workType: 'DAILY', workStartTime: '', workEndTime: ''})
                                                    }
                                                />
                                                <label htmlFor="radio1">종일제</label>
                                            </span>
                                            <span className="text-radio">
                                                <input
                                                    type="radio"
                                                    id="radio2"
                                                    name="workType"
                                                    checked={form.workType === 'HOURLY'}
                                                    onChange={() =>
                                                        updateForm({workType: 'HOURLY'})
                                                    }
                                                />
                                                <label htmlFor="radio2">시간제</label>
                                            </span>
                                        </div>
                                        {form.workType === 'HOURLY' && (
                                            <div className="search-date-box mt10">
                                                <div className="date-input">
                                                    <select
                                                        value={form.workStartTime}
                                                        onChange={e =>
                                                            updateForm({workStartTime: e.target.value})
                                                        }
                                                    >
                                                        <option value="" hidden>시작 시간</option>
                                                        {Array.from({length: 48}, (_, i) => {
                                                            const hour = Math.floor(i / 2);
                                                            const minute = (i % 2) * 30;
                                                            const time = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
                                                            return (
                                                                <option key={`start-${time}`} value={time}>
                                                                    {time}
                                                                </option>
                                                            );
                                                        })}
                                                    </select>
                                                </div>
                                                <p>~</p>
                                                <div className="date-input">
                                                    <select
                                                        value={form.workEndTime || ''}
                                                        onChange={e =>
                                                            updateForm({workEndTime: e.target.value})
                                                        }
                                                    >
                                                        <option value="" hidden>종료 시간</option>
                                                        {Array.from({length: 48}, (_, i) => {
                                                            const hour = Math.floor(i / 2);
                                                            const minute = (i % 2) * 30;
                                                            const time = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
                                                            return (
                                                                <option key={`end-${time}`} value={time}>
                                                                    {time}
                                                                </option>
                                                            );
                                                        })}
                                                    </select>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* 돌봄 기간 */}
                            <div className="round-box">
                                <div className="round-header">
                                    <h3 className="round-tit">돌봄 기간</h3>
                                </div>
                                <div className="round-body">
                                    <div className="form-item">
                                        <div className="search-date-box">
                                            <div className="date-input">
                                                <input
                                                    type="date"
                                                    value={form.careStartDate}
                                                    min={(() => {
                                                        const d = new Date();
                                                        if (form.workType !== 'HOURLY') d.setDate(d.getDate() + 1);
                                                        return d.toISOString().split('T')[0];
                                                    })()}
                                                    onChange={e =>
                                                        updateForm({careStartDate: e.target.value})
                                                    }
                                                />
                                            </div>
                                            <p>~</p>
                                            <div className="date-input">
                                                <input
                                                    type="date"
                                                    value={form.careEndDate}
                                                    min={form.careStartDate || (() => {
                                                        const d = new Date();
                                                        if (form.workType !== 'HOURLY') d.setDate(d.getDate() + 1);
                                                        return d.toISOString().split('T')[0];
                                                    })()}
                                                    onChange={e =>
                                                        updateForm({careEndDate: e.target.value})
                                                    }
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* 돌봄 요청 장소 */}
                            <div className="round-box">
                                <div className="round-header">
                                    <h3 className="round-tit">돌봄 요청 장소</h3>
                                </div>
                                <div className="round-body">
                                    <div className="form-item">
                                        <div className="tab-btn-row">
                                            <button
                                                type="button"
                                                className={`tab-btn ${form.carePlaceType === 'HOME' ? 'active' : ''}`}
                                                onClick={() =>
                                                    updateForm({carePlaceType: 'HOME', hospitalName: ''})
                                                }
                                            >
                                                집
                                            </button>
                                            <button
                                                type="button"
                                                className={`tab-btn ${form.carePlaceType === 'HOSPITAL' ? 'active' : ''}`}
                                                onClick={() =>
                                                    updateForm({carePlaceType: 'HOSPITAL'})
                                                }
                                            >
                                                병원
                                            </button>
                                        </div>

                                        {form.carePlaceType === 'HOSPITAL' && (
                                            <div className="input-row mt10">
                                                <label className="input-tit">병원명</label>
                                                <div className="input-box">
                                                    <input
                                                        type="text"
                                                        value={form.hospitalName}
                                                        placeholder="병원명을 입력해주세요"
                                                        onChange={e =>
                                                            updateForm({hospitalName: e.target.value})
                                                        }
                                                    />
                                                </div>
                                            </div>
                                        )}

                                        <div className="input-row mt10">
                                            <label className="input-tit">주소</label>
                                            <div style={{flex: 1, minWidth: 0}}>
                                                <div className="address-search-row">
                                                    <div className="input-box">
                                                        <input
                                                            type="text"
                                                            value={roadAddress || ''}
                                                            readOnly
                                                            placeholder="주소를 검색해주세요"
                                                            onClick={() => setShowPostcode(true)}
                                                        />
                                                    </div>
                                                    <button
                                                        type="button"
                                                        className="btn-address-search"
                                                        onClick={() => setShowPostcode(true)}
                                                    >
                                                        검색
                                                    </button>
                                                </div>
                                            </div>
                                        </div>

                                        {roadDetail && (
                                            <div className="input-row mt5">
                                                <label className="input-tit" style={{visibility: 'hidden'}}>주소</label>
                                                <div className="input-box">
                                                    <input
                                                        type="text"
                                                        value={roadDetail}
                                                        readOnly
                                                        style={{background: '#f5f5f5', color: '#666'}}
                                                    />
                                                </div>
                                            </div>
                                        )}

                                        <div className="input-row mt5">
                                            <label className="input-tit">상세주소</label>
                                            <div className="input-box">
                                                <input
                                                    type="text"
                                                    value={userDetail}
                                                    placeholder="상세 주소를 입력해주세요 (건물명, 병원 호실 등)"
                                                    maxLength={100}
                                                    onChange={e => handleUserDetailChange(e.target.value)}
                                                />
                                            </div>
                                        </div>
                                        <p className="guide_txt mt5" style={{color: '#999', fontSize: '12px'}}>
                                            * 상세 주소는 필수입력정보입니다. (건물명, 병원 호실 등)
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* 다음 주소 검색 — 전체 화면 (Portal로 body에 직접 렌더링해 iOS 스태킹 컨텍스트 문제 우회) */}
                            {showPostcode && createPortal(
                                <div className="postcode-overlay">
                                    <div className="postcode-modal">
                                        <div className="postcode-header">
                                            <h4>주소 검색</h4>
                                            <button type="button" onClick={() => setShowPostcode(false)}>✕</button>
                                        </div>
                                        <div className="postcode-content">
                                            <DaumPostcode
                                                onComplete={handlePostcodeComplete}
                                                style={{width: '100%', height: '100%'}}
                                                scriptUrl="https://t1.kakaocdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js"
                                            />
                                        </div>
                                    </div>
                                </div>,
                                document.body
                            )}

                            {/* 어르신 상태 정보 */}
                            {renderQuestionSection()}

                            {/* 돌봄 급여 */}
                            <div className="round-box">
                                <div className="round-header">
                                    <h3 className="round-tit">돌봄 급여</h3>
                                </div>
                                <div className="round-body">
                                    <div className="form-item">
                                        <div className="radio-row">
                                            <span className="text-radio">
                                                <input
                                                    type="radio"
                                                    id="radio5"
                                                    name="payNegotiable"
                                                    checked={form.payNegotiable === 'Y'}
                                                    onChange={() =>
                                                        updateForm({payNegotiable: 'Y'})
                                                    }
                                                />
                                                <label htmlFor="radio5">협의 가능</label>
                                            </span>
                                            <span className="text-radio">
                                                <input
                                                    type="radio"
                                                    id="radio6"
                                                    name="payNegotiable"
                                                    checked={form.payNegotiable === 'N'}
                                                    onChange={() =>
                                                        updateForm({payNegotiable: 'N'})
                                                    }
                                                />
                                                <label htmlFor="radio6">협의 불가능</label>
                                            </span>
                                        </div>

                                        <div className="input-row mt15">
                                            <label className="input-tit">
                                                {form.workType === 'DAILY' ? '일급' : '시급'}
                                            </label>
                                            <div className="input-box pr10">
                                                <input
                                                    inputMode="numeric"
                                                    pattern="[0-9]*"
                                                    type="text"
                                                    className="text-right"
                                                    value={setComma(form.dailyPay)}
                                                    onChange={e => {
                                                        const rawValue = unComma(e.target.value).toString();
                                                        updateForm({dailyPay: rawValue})
                                                    }
                                                    }
                                                />
                                                <span>원</span>
                                            </div>
                                        </div>

                                        <p className="text-right guide_txt mt10 point">
                                            평균 임금 {getAverageWage()}입니다.
                                        </p>
                                        {getWageRange() && (
                                            <p className="text-right guide_txt mt5" style={{color: '#666'}}>
                                                입력 가능한 돌봄 급여: {getWageRange()!.min.toLocaleString()}원
                                                ~ {getWageRange()!.max.toLocaleString()}원
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                </section>
            </main>

            <div
                className="bottom-btn"
                style={(keyboardHeight > 0 || showPostcode) ? {display: 'none'} : undefined}
            >
                <button type="button" className="btn cancel" onClick={onPrevStep}>
                    이전
                </button>
                <button type="button" className="btn confirm" onClick={handleSubmit}>
                    공고등록
                </button>
            </div>
        </>
    );
};

export default JobPostingStep2;